import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {lazy, Suspense, useState} from 'react';

import * as analytics from 'analytics/Analytics';
import {setWindowTitle} from 'BenchlingUtil';
import {CAPILLARY_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import BenchlingLayoutPlugin from './plugins/BenchlingLayoutPlugin';
import ExtendedFilterPlugin from './plugins/ExtendedFilterPlugin';
import {AvailabilityToShow} from './swagger-ui/constants';
import {getBearerToken} from './swagger-ui/util';
import SwaggerUiPageHeader from './SwaggerUiPageHeader';

export const defaultVersion = 'v2';
export const defaultAvailability = AvailabilityToShow.GA;
interface Props {
  version: string | null;
  availabilityToShow: AvailabilityToShow | null;
}

export function trackTryItNow(method: string): void {
  analytics.deprecatedTrackEvent(CAPILLARY_ANALYTICS_ACTIONS.CALLED_TRY_IT_NOW, {
    method,
  });
}

async function tryItNowRequestInterceptor(request: Request): Promise<Request> {
  trackTryItNow(request.method);
  /*
   * Avoid auth header and instead rely on user-supplied POST body containing client ID and secret
   * on post /api/v2/token endpoint
   */
  if (request.url.includes('/api/v2/token')) {
    return request;
  }
  // @ts-expect-error This is a non-standard parameter set by swagger when we're loading the spec.
  if (request.loadSpec) {
    return request;
  }
  const bearerToken = await getBearerToken();
  // @ts-expect-error Default TypeScript interfaces do not support setting Request headers.
  request.headers.Authorization = `Bearer ${bearerToken}`;
  return request;
}
function getAvailabilityParam(availabilityToShow: AvailabilityToShow): string {
  if (availabilityToShow === defaultAvailability) {
    return '';
  }
  return `?availability=${availabilityToShow}`;
}

export function getSpecFilePath(version: string, availabilityToShow: AvailabilityToShow): string {
  return `/api/${version}/openapi.yaml${getAvailabilityParam(availabilityToShow)}`;
}

export function getURLPath(version: string, availabilityToShow: AvailabilityToShow): string {
  if (version === defaultVersion) {
    return `/api/reference${getAvailabilityParam(availabilityToShow)}`;
  }
  return `/api/${version}/reference${getAvailabilityParam(availabilityToShow)}`;
}

const DROPDOWN_LABEL_BY_AVAILABILITY = {
  [AvailabilityToShow.GA]: '',
  [AvailabilityToShow.LA]: ' (Pre-release)',
  [AvailabilityToShow.NOT_AVAILABLE]: ' (Not available)',
};
export function getDropdownLabel(version: string, availabilityToShow: AvailabilityToShow): string {
  return `${version}${DROPDOWN_LABEL_BY_AVAILABILITY[availabilityToShow]}`;
}

const SwaggerUI = lazy(() => import(/* webpackChunkName: "swagger-ui" */ 'swagger-ui-react'));

export default function SwaggerUiPage(props: Props): JSX.Element {
  React.useEffect(() => {
    setWindowTitle('Benchling API Documentation');
  }, []);
  const initialVersion = props.version || defaultVersion;
  const initialAvailabilityToShow = props.availabilityToShow || AvailabilityToShow.GA;

  const [version, setVersion] = useState<string>(initialVersion);
  const [availabilityToShow, setAvailabilityToShow] = useState<AvailabilityToShow>(initialAvailabilityToShow);

  const userSignedIn = window.bootstrapUser != null;

  function updateDropdown(version: string, availabilityToShow: AvailabilityToShow): void {
    window.history.pushState({}, '', getURLPath(version, availabilityToShow));
    setVersion(version);
    setAvailabilityToShow(availabilityToShow);
  }

  return (
    <div>
      <SwaggerUiPageHeader
        updateDropdown={updateDropdown}
        dropdownLabel={getDropdownLabel(version, availabilityToShow)}
      />
      <div className={classNames(css(styles.contentWrapper))}>
        <Suspense fallback={<CenteredHorizontalThrobber />}>
          <SwaggerUI
            url={getSpecFilePath(version, availabilityToShow)}
            plugins={[BenchlingLayoutPlugin, ExtendedFilterPlugin]}
            requestInterceptor={userSignedIn ? tryItNowRequestInterceptor : (req) => req}
            docExpansion="none"
            deepLinking={true}
            defaultModelExpandDepth={3}
            showExtensions={true}
          />
        </Suspense>
      </div>
    </div>
  );
}
const styles = StyleSheet.create({
  contentWrapper: {
    margin: '40px auto',
    width: '90%',
    maxWidth: '1300px',
  },
});
